import { apiClient } from "@shared/lib/api-client";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { InferRequestType } from "hono";

export const createCustomerPortalLinkMutationKey = [
	"create-customer-portal-link",
] as const;
export const useCreateCustomerPortalLinkMutation = () => {
	return useMutation({
		mutationKey: createCustomerPortalLinkMutationKey,
		mutationFn: async (
			query: InferRequestType<
				(typeof apiClient.payments)["create-customer-portal-link"]["$post"]
			>["query"],
		) => {
			const response = await apiClient.payments[
				"create-customer-portal-link"
			].$post({
				query,
			});

			if (!response.ok) {
				throw new Error("Failed to create customer portal link");
			}

			return response.json();
		},
	});
};

export const createCheckoutLinkMutationKey = ["create-checkout-link"] as const;
export const useCreateCheckoutLinkMutation = () => {
	return useMutation({
		mutationKey: createCheckoutLinkMutationKey,
		mutationFn: async (
			query: InferRequestType<
				(typeof apiClient.payments)["create-checkout-link"]["$post"]
			>["query"],
		) => {
			const response = await apiClient.payments[
				"create-checkout-link"
			].$post({
				query,
			});

			if (!response.ok) {
				throw new Error("Failed to create checkout link");
			}

			return response.json();
		},
	});
};

export const purchasesQueryKey = (organizationId?: string) =>
	organizationId
		? ["organization", "purchases", organizationId]
		: (["user", "purchases"] as const);
export const usePurchasesQuery = (
	organizationId?: string,
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey: purchasesQueryKey(organizationId),
		queryFn: async () => {
			const response = await apiClient.payments.purchases.$get({
				query: {
					organizationId,
				},
			});

			if (!response.ok) {
				throw new Error("Failed to fetch purchases");
			}

			return response.json();
		},
		...options,
	});
};

// Cancel subscription mutation
export const cancelSubscriptionMutationKey = ["cancel-subscription"] as const;
export const useCancelSubscriptionMutation = () => {
	return useMutation({
		mutationKey: cancelSubscriptionMutationKey,
		mutationFn: async (query: {
			subscriptionId: string;
			reason?: string;
		}) => {
			const response = await apiClient.payments[
				"cancel-subscription"
			].$post({
				query,
			});

			if (!response.ok) {
				throw new Error("Failed to cancel subscription");
			}

			return response.json();
		},
	});
};

// Cancellation feedback without cancelling (retention flow "keep my plan")
export const cancelFeedbackMutationKey = ["cancel-feedback"] as const;
export const useCancelFeedbackMutation = () => {
	return useMutation({
		mutationKey: cancelFeedbackMutationKey,
		mutationFn: async (json: {
			subscriptionId: string;
			reasons: string[];
			message?: string;
		}) => {
			const response = await apiClient.payments["cancel-feedback"].$post({
				json,
			});

			if (!response.ok) {
				throw new Error("Failed to send cancellation feedback");
			}
		},
	});
};

// Resume subscription mutation
export const resumeSubscriptionMutationKey = ["resume-subscription"] as const;
export const useResumeSubscriptionMutation = () => {
	return useMutation({
		mutationKey: resumeSubscriptionMutationKey,
		mutationFn: async (query: {
			subscriptionId: string;
		}) => {
			const response = await apiClient.payments[
				"resume-subscription"
			].$post({
				query,
			});

			if (!response.ok) {
				throw new Error("Failed to resume subscription");
			}

			return response.json();
		},
	});
};

// Change subscription plan mutation
export const changeSubscriptionPlanMutationKey = [
	"change-subscription-plan",
] as const;
export const useChangeSubscriptionPlanMutation = () => {
	return useMutation({
		mutationKey: changeSubscriptionPlanMutationKey,
		mutationFn: async (query: {
			subscriptionId: string;
			newProductId: string;
			immediately?: boolean;
			redirectUrl?: string;
		}) => {
			const response = await apiClient.payments[
				"change-subscription-plan"
			].$post({
				query: {
					...query,
					immediately:
						query.immediately === undefined
							? undefined
							: String(query.immediately),
				},
			});

			if (!response.ok) {
				const body = await response.json().catch(() => null);
				throw new Error(
					body && "message" in body
						? body.message
						: "Failed to change subscription plan",
				);
			}

			return response.json();
		},
	});
};
